/* Dónde está cada uno, solo si quiere. Tú decides cuándo y cuánto: «Enviar ahora» (una vez) o «Compartir en vivo»
   1, 3 u 8 horas. En vivo, la posición se manda cada 2 minutos o cuando te mueves 100 m, SOLO con la app abierta:
   una web no puede leer el GPS en segundo plano. Si cierras la app, en el servidor se queda tu última posición
   (con su hora) hasta que acabe el tiempo que elegiste; al dejar de compartir se borra. */
import { useSyncExternalStore } from 'react';
import { noAutorizado, registrarAntesDeSalir } from './sesion.ts';
import { VIAJE } from './viaje.ts';
import { km } from './mapa.ts';

export interface Compartida { usuario: string; nombre: string; lat: number; lon: number; precision: number | null; actualizado: number; hasta: number }
interface Estado { otros: Compartida[]; yo: { actualizado: number; hasta: number } | null; error: string | null; viva: boolean }

const CLAVE = 'tabiji.compartir.hasta';
const CADA_MS = 120_000, DISTANCIA_M = 100, UNA_VEZ_MS = 2 * 3600_000;
const RUTA = `/api/viajes/${VIAJE}`;

let estado: Estado = { otros: [], yo: null, error: null, viva: false };
const oyentes = new Set<() => void>();
const cambiar = (n: Partial<Estado>) => { estado = { ...estado, ...n }; oyentes.forEach(f => f()); };
export const useUbicacion = (): Estado => useSyncExternalStore(f => { oyentes.add(f); return () => oyentes.delete(f); }, () => estado);

const leerHasta = (): number => { try { return Number(localStorage.getItem(CLAVE)) || 0; } catch { return 0; } };
const guardarHasta = (h: number | null) => { try { if (h) localStorage.setItem(CLAVE, String(h)); else localStorage.removeItem(CLAVE); } catch { /* sin almacenamiento */ } };

let vigilante: number | null = null, temporizador = 0;
let ultima: { lat: number; lon: number; precision: number | null } | null = null;
let enviado: { lat: number; lon: number; t: number } | null = null;

async function llamar(metodo: string, ruta: string, cuerpo?: unknown): Promise<Response | null> {
  try {
    const r = await fetch(ruta, { method: metodo, headers: cuerpo ? { 'Content-Type': 'application/json' } : undefined, body: cuerpo ? JSON.stringify(cuerpo) : undefined, signal: AbortSignal.timeout(15_000) });
    if (r.status === 401) noAutorizado();
    return r;
  } catch { return null; }
}

/** Los demás (y tú) tal como están ahora en el servidor. */
export async function refrescar() {
  const r = await llamar('GET', `${RUTA}/ubicaciones`);
  if (!r?.ok) return;
  const d = await r.json();
  cambiar({ otros: d.otros, yo: d.yo });
}

async function enviar(hasta: number) {
  if (!ultima) return;
  enviado = { lat: ultima.lat, lon: ultima.lon, t: Date.now() };
  const r = await llamar('POST', `${RUTA}/ubicacion`, { ...ultima, hasta });
  if (r?.ok) { const d = await r.json(); cambiar({ yo: { actualizado: Date.now(), hasta: d.hasta }, error: null }); }
  else if (r) cambiar({ error: 'No se pudo enviar tu ubicación' });
}

const errorGps = (e: GeolocationPositionError) =>
  e.code === 1 ? 'Sin permiso de ubicación: actívalo para Tabiji en el navegador.' : 'No llega la señal del GPS.';

function parar() {
  if (vigilante != null) navigator.geolocation.clearWatch(vigilante);
  vigilante = null;
  clearInterval(temporizador);
  cambiar({ viva: false });
}

/** Mientras haya tiempo por delante, se vigila el GPS y se manda cada 2 min o al moverte 100 m. */
function vigilar() {
  if (vigilante != null || !('geolocation' in navigator)) return;
  const tic = () => {
    const hasta = leerHasta();
    if (!hasta || Date.now() >= hasta) { void dejarDeCompartir(); return; }
    if (ultima && (!enviado || Date.now() - enviado.t >= CADA_MS)) void enviar(hasta);
  };
  const ahoraMismo = () => { const hasta = leerHasta(); if (hasta && Date.now() < hasta) void enviar(hasta); };
  vigilante = navigator.geolocation.watchPosition(
    p => {
      ultima = { lat: p.coords.latitude, lon: p.coords.longitude, precision: Math.round(p.coords.accuracy) };
      if (!enviado || km([enviado.lon, enviado.lat], [ultima.lon, ultima.lat]) * 1000 > DISTANCIA_M) ahoraMismo();
    },
    e => cambiar({ error: errorGps(e) }),
    { enableHighAccuracy: true, maximumAge: 15_000, timeout: 30_000 });
  temporizador = window.setInterval(tic, 30_000);
  cambiar({ viva: true, error: null });
}

/** Compartir en vivo durante `minutos` (la primera posición sale en cuanto llega). */
export function compartirEnVivo(minutos: number) {
  if (!('geolocation' in navigator)) { cambiar({ error: 'Este móvil no da la ubicación.' }); return; }
  guardarHasta(Date.now() + minutos * 60_000);
  cambiar({ error: null });
  vigilar();
}

/** Una sola vez: tu posición de ahora, visible un par de horas con su «hace X min». */
export function enviarAhora() {
  if (!('geolocation' in navigator)) { cambiar({ error: 'Este móvil no da la ubicación.' }); return; }
  navigator.geolocation.getCurrentPosition(
    p => {
      ultima = { lat: p.coords.latitude, lon: p.coords.longitude, precision: Math.round(p.coords.accuracy) };
      const vivo = leerHasta();
      void enviar(vivo && vivo > Date.now() ? vivo : Date.now() + UNA_VEZ_MS);
    },
    e => cambiar({ error: errorGps(e) }),
    { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 });
}

export async function dejarDeCompartir() {
  guardarHasta(null);
  parar();
  ultima = null; enviado = null;
  await llamar('DELETE', `${RUTA}/ubicacion`);
  cambiar({ yo: null });
}

// Al cerrar sesión se deja de compartir ANTES (con la sesión aún válida, para que el servidor borre tu posición)
registrarAntesDeSalir(() => dejarDeCompartir());

/** Al abrir la app: si estabas compartiendo en vivo y aún queda tiempo, se retoma. */
export function arrancarUbicacion() {
  const hasta = leerHasta();
  if (hasta && Date.now() < hasta) vigilar(); else if (hasta) guardarHasta(null);
  void refrescar();
  const alVolver = () => { if (document.visibilityState === 'visible') { void refrescar(); const h = leerHasta(); if (h && Date.now() < h) vigilar(); } };
  document.addEventListener('visibilitychange', alVolver);
  return () => { document.removeEventListener('visibilitychange', alVolver); parar(); };
}

/** «hace 4 min», «hace 1 h 12 min». */
export function hace(ms: number): string {
  const m = Math.max(0, Math.round((Date.now() - ms) / 60_000));
  if (m < 1) return 'ahora mismo';
  if (m < 60) return `hace ${m} min`;
  return `hace ${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
}
